import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useVerificationCodeByEmail, useVerificationCodeByPhone } from '@api/auth';
import { useLoginHelper } from '@api/auth/helpers';

import { useAuth } from '@context/auth';
import { useCartContext } from '@context/cart';

import { publicEnv } from '@utils/config';

import SendCodeByEmail from '@components/NewAuth/SendCodeByEmail';
import SendCodeByPhone from '@components/NewAuth/SendCodeByPhone';
import { SignIn } from '@components/NewAuth/SignIn';
import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';
import { Modal } from '@components/uiKit/Modal';

import { YANDEX_COUNTER_ID } from '@scripts/data/different';
import { useMatchMedia } from '@scripts/hooks';

import styles from './NewAuth.module.scss';
import { AuthPopupProps, RegisterParams } from './types';
import { resolveCloseButtonText, resolveConfirmButtonText, resolveModalTitle } from './utils';

const TIMER_DURATION = 60;
const MOBILE_MAX_WIDTH = 767;
export const AUTH_SUCCESS_DELAY_MS = 1000;

const RegisterForm = dynamic(() => import('./RegisterForm'), { ssr: false });

const AuthPopup: FC<AuthPopupProps> = ({ isOpen, closeHandler, isRegistration = false, redirectPath = undefined }) => {
    const { push } = useRouter();
    const isMobileFullWidth = useMatchMedia({ maxWidth: MOBILE_MAX_WIDTH }, false);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const { isAuthUser } = useAuth();
    const [registerInfo, setRegisterInfo] = useState();
    const [isSignIn, setIsSignIn] = useState<boolean>(!isRegistration);
    const [isLogin, setIsLogin] = useState<boolean>(false);
    const [isSendCodeByPhone, setIsSendCodeByPhone] = useState<boolean>(false);
    const [isSendCodeByEmail, setIsSendCodeByEmail] = useState<boolean>(false);
    const [isRegistrationForm, setIsRegistrationForm] = useState<boolean>(isRegistration);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [isCanSendAgain, setIsCanSendAgain] = useState<boolean>(true);
    const [timerRef, setTimerRef] = useState<NodeJS.Timeout | null>(null);
    const { basketProducts } = useCartContext();
    const formSubmitRef = useRef<(() => Promise<void> | void) | null>(null);

    const bindFormSubmit = useCallback((submit: (() => Promise<void> | void) | null) => {
        formSubmitRef.current = submit;
    }, []);

    const handleModalSubmit = useCallback(() => formSubmitRef.current?.(), []);

    const { loginCodeByPhoneHelper, loginCodeByEmailHelper, registerByCodeHelper } = useLoginHelper();

    const finishSuccessfulAuth = useCallback(() => {
        setTimeout(() => {
            if (redirectPath) {
                push(redirectPath);
            }

            closeHandler();
        }, AUTH_SUCCESS_DELAY_MS);
    }, [closeHandler, push, redirectPath]);

    const logInByPhone = useCallback(
        async (logInData: { phone: string; code: string }) => {
            try {
                const response = await loginCodeByPhoneHelper(logInData, setIsLoading);

                if (response?.error) {
                    setIsLoading(false);
                    setErrorMessage(response.error);

                    return;
                }

                setErrorMessage('');
                finishSuccessfulAuth();
            } catch (error: any) {
                setIsLoading(false);
                setErrorMessage(error.message);
            }
        },
        [finishSuccessfulAuth, loginCodeByPhoneHelper]
    );

    const logInByEmail = useCallback(
        async (logInData: { email: string; code: string }) => {
            try {
                const response = await loginCodeByEmailHelper(logInData, setIsLoading);

                if (response?.error) {
                    setIsLoading(false);
                    setErrorMessage(response.error);

                    return;
                }

                setErrorMessage('');
                finishSuccessfulAuth();
            } catch (error: any) {
                setIsLoading(false);
                setErrorMessage(error.message);
            }
        },
        [finishSuccessfulAuth, loginCodeByEmailHelper]
    );

    const registerByCode = useCallback(
        async (logInData: RegisterParams) => {
            try {
                await registerByCodeHelper(logInData, setIsLoading);

                if (basketProducts.length === 0) closeHandler();
                setErrorMessage('');

                if (typeof window.ym === 'function') {
                    window.ym(YANDEX_COUNTER_ID, 'reachGoal', 'new_registration');
                }

                finishSuccessfulAuth();
            } catch (error: any) {
                setIsLoading(false);
                setErrorMessage(error.message);
            }
        },
        [basketProducts.length, closeHandler, finishSuccessfulAuth, registerByCodeHelper]
    );

    const { mutateAsync: verificationCodeByEmail } = useVerificationCodeByEmail();

    const handlePopupClose = useCallback(() => {
        closeHandler();
        setIsLoading(false);
        setIsSignIn(!isRegistration);
        setIsRegistrationForm(isRegistration);
        setIsSendCodeByPhone(false);
        setIsSendCodeByEmail(false);
        setErrorMessage('');
        setEmail('');
        setPhone('');
    }, [closeHandler, isRegistration]);

    const toggleToPhoneLogin = useCallback(() => {
        setIsRegistrationForm(false);
        setIsSignIn(false);
        setIsSendCodeByPhone(true);
        setIsSendCodeByEmail(false);
    }, []);

    const backToSignIn = useCallback(() => {
        setIsSendCodeByPhone(false);
        setIsSendCodeByEmail(false);
        setIsRegistrationForm(false);
        setIsSignIn(true);
        setErrorMessage('');
    }, []);

    const backToRegistration = useCallback(() => {
        setIsSendCodeByPhone(false);
        setIsSendCodeByEmail(false);
        setIsSignIn(false);
        setIsRegistrationForm(true);
        setErrorMessage('');
    }, []);

    const handleBack = useCallback(() => {
        if (isSendCodeByPhone || isSendCodeByEmail) {
            if (isLogin) {
                backToSignIn();
            } else {
                backToRegistration();
            }

            return;
        }

        if (isRegistrationForm) {
            setIsRegistrationForm(false);
            setIsSignIn(true);
            setErrorMessage('');

            return;
        }

        if (isSignIn) {
            handlePopupClose();
        }
    }, [
        isSendCodeByPhone,
        isSendCodeByEmail,
        isLogin,
        isRegistrationForm,
        isSignIn,
        backToSignIn,
        backToRegistration,
        handlePopupClose,
    ]);

    const switchToCodeInput = useCallback((type: 'phone' | 'email') => {
        setIsSignIn(false);
        setIsRegistrationForm(false);
        if (type === 'phone') {
            setIsSendCodeByPhone(true);
            setIsSendCodeByEmail(false);
        } else {
            setIsSendCodeByEmail(true);
            setIsSendCodeByPhone(false);
        }
    }, []);

    const sendCodeByEmailForRegistration = useCallback(
        async (email: string) => {
            try {
                await verificationCodeByEmail({ email });
                setIsSendCodeByPhone(false);
                setIsSendCodeByEmail(true);
                setIsLogin(false);
                setErrorMessage('');
            } catch (error: any) {
                setErrorMessage(error.message);
            }
        },
        [verificationCodeByEmail]
    );

    const { mutateAsync: verificationCodeByPhone } = useVerificationCodeByPhone();

    const startTimer = useCallback(() => {
        if (timerRef) {
            clearInterval(timerRef);
        }

        setTimeLeft(TIMER_DURATION);
        setIsCanSendAgain(false);

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setIsCanSendAgain(true);

                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        setTimerRef(timer);
    }, [timerRef]);

    const stopTimer = useCallback(() => {
        if (timerRef) {
            clearInterval(timerRef);
            setTimerRef(null);
        }
        setTimeLeft(0);
        setIsCanSendAgain(true);
    }, [timerRef]);

    const sendCodeByPhoneForRegistration = useCallback(
        async (phone: string) => {
            try {
                await verificationCodeByPhone({ phone });
                setIsSendCodeByEmail(false);
                setIsSendCodeByPhone(true);
                setIsLogin(false);
                setErrorMessage('');
            } catch (error: any) {
                setErrorMessage(error.message);
            }
        },
        [verificationCodeByPhone]
    );

    useEffect(() => {
        if (isOpen && isAuthUser && redirectPath) {
            closeHandler();
            push(redirectPath);
        }
    }, [isOpen, isAuthUser, redirectPath, closeHandler, push]);

    useEffect(() => {
        return () => {
            if (timerRef) {
                clearInterval(timerRef);
            }
        };
    }, [timerRef]);

    useEffect(() => {
        if (isOpen) {
            setIsLoading(false);
            setIsSignIn(!isRegistration);
            setIsRegistrationForm(isRegistration);
            setIsSendCodeByPhone(false);
            setIsSendCodeByEmail(false);
            setErrorMessage('');
            setEmail('');
            setPhone('');
        }
    }, [isOpen, isRegistration]);

    const isCodeStep = isSendCodeByPhone || isSendCodeByEmail;
    const isCenteredHeader = isMobileFullWidth || !isSignIn;
    const modalTitle = resolveModalTitle({ isCodeStep, isRegistrationForm, isSignIn });
    const confirmButtonText = resolveConfirmButtonText({
        isLoading,
        isSignIn,
        isRegistrationForm,
        isCodeStep,
        isCanSendAgain,
    });
    const closeButtonText = resolveCloseButtonText({
        isLoading,
        isRegistrationForm,
        isSendCodeByPhone,
        isSendCodeByEmail,
        isCanSendAgain,
        isLogin,
    });

    const handleCloseButtonClick = useCallback(() => {
        setErrorMessage('');

        if (isRegistrationForm) {
            setIsRegistrationForm(false);
            setIsSignIn(true);

            return;
        }

        if (isSendCodeByPhone) {
            if (isLogin) {
                backToSignIn();
            } else {
                void sendCodeByEmailForRegistration(email);
            }

            return;
        }

        if (isSendCodeByEmail) {
            if (isLogin) {
                backToSignIn();
            } else {
                void sendCodeByPhoneForRegistration(phone);
            }
        }
    }, [
        backToSignIn,
        email,
        isLogin,
        isRegistrationForm,
        isSendCodeByEmail,
        isSendCodeByPhone,
        phone,
        sendCodeByEmailForRegistration,
        sendCodeByPhoneForRegistration,
    ]);

    if (publicEnv.technicalPause) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handlePopupClose}
            title={modalTitle}
            showCloseButton={!modalTitle}
            closeButtonAriaLabel="Закрыть"
            width={isSendCodeByEmail || isSendCodeByPhone ? 500 : 450}
            mobileType="fullWidth"
            className={styles.modal}
            customCss={{
                contentClassName: styles.modalContent,
                confirmButtonWrapper: { width: '100%' },
                footerLine: {
                    width: '100%',
                    flexDirection: 'column-reverse',
                    alignItems: 'stretch',
                    gap: 12,
                },
            }}
            withIconBack={isCenteredHeader}
            onBack={handleBack}
            titleCenteredAlways={isCenteredHeader}
            isFooterLine={isMobileFullWidth && Boolean(confirmButtonText || closeButtonText)}
            confirmButtonText={confirmButtonText || undefined}
            closeButtonText={closeButtonText}
            onCloseButtonClick={closeButtonText ? handleCloseButtonClick : undefined}
            handlerSubmit={handleModalSubmit}
        >
            <div className={styles.body}>
                {isLoading && <LoadingSpinner embedded noPadding />}
                {isRegistrationForm && !isLoading && (
                    <RegisterForm
                        bindSubmit={bindFormSubmit}
                        hidePrimaryActions
                        setIsLogin={setIsLogin}
                        onChangePhoneToLogin={() => switchToCodeInput('phone')}
                        onChangeEmailToLogin={() => switchToCodeInput('email')}
                        setRegisterInfo={setRegisterInfo}
                        phone={phone}
                        email={email}
                        setPhone={setPhone}
                        setEmail={setEmail}
                        closeHandler={closeHandler}
                        toggleForm={toggleToPhoneLogin}
                        onChangeToSignIn={() => {
                            setIsRegistrationForm(false);
                            setIsSignIn(true);
                        }}
                        onStartTimer={startTimer}
                    />
                )}

                {isSignIn && !isLoading && (
                    <SignIn
                        bindSubmit={bindFormSubmit}
                        hidePrimaryActions
                        setIsLogin={setIsLogin}
                        onChangeEmailToLogin={() => switchToCodeInput('email')}
                        onChangePhoneToLogin={() => switchToCodeInput('phone')}
                        setEmail={setEmail}
                        setPhone={setPhone}
                        onChangeToRegistration={() => {
                            setIsSignIn(false);
                            setIsRegistrationForm(true);
                        }}
                        logIn={logInByPhone}
                        closeHandler={closeHandler}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        onStartTimer={startTimer}
                    />
                )}

                {isSendCodeByPhone && !isLoading && (
                    <SendCodeByPhone
                        bindSubmit={bindFormSubmit}
                        hidePrimaryActions
                        setPhone={setPhone}
                        register={registerByCode}
                        email={email}
                        phone={phone}
                        isLogin={isLogin}
                        registerInfo={registerInfo}
                        logIn={logInByPhone}
                        backToSignin={backToSignIn}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        sendCodeByEmailForRegistration={sendCodeByEmailForRegistration}
                        startTimer={startTimer}
                        stopTimer={stopTimer}
                        timeLeft={timeLeft}
                        isCanSendAgain={isCanSendAgain}
                    />
                )}
                {isSendCodeByEmail && !isLoading && (
                    <SendCodeByEmail
                        bindSubmit={bindFormSubmit}
                        hidePrimaryActions
                        isLogin={isLogin}
                        register={registerByCode}
                        registerInfo={registerInfo}
                        logIn={logInByEmail}
                        email={email}
                        phone={phone}
                        setEmail={setEmail}
                        backToSignin={backToSignIn}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        sendCodeByPhoneForRegistration={sendCodeByPhoneForRegistration}
                        startTimer={startTimer}
                        stopTimer={stopTimer}
                        timeLeft={timeLeft}
                        isCanSendAgain={isCanSendAgain}
                    />
                )}
            </div>
        </Modal>
    );
};

export default AuthPopup;
